import React, { useState, useEffect } from 'react';
import ReactShadowRoot from 'react-shadow-root';
import Popover from '@mui/material/Popover';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import { colours } from '../../theme/constants';
import EditCampaignLinkDialog from '../../superAdmin/campaigns/EditCampaignLinkDialog';

const pageUrlPatterns = [
    /^https?:\/\/([\w-]+\.)*newzapp\.co\.uk\/t\/page\/click/,
    /^https?:\/\/([\w-]+\.)*nz-dev\.co\.uk\/t\/page\/click/,
    /^https?:\/\/localhost(:\d+)?\/t\/page\/click/
];

const emailUrlPatterns = [
    /^https?:\/\/([\w-]+\.)*newzapp\.co\.uk\/t\/click/,
    /^https?:\/\/([\w-]+\.)*nz-dev\.co\.uk\/t\/click/,
    /^https?:\/\/localhost(:\d+)?\/t\/click/
];

const idPrefix = 'newzapp-hotspot-';

const style = `<style type="text/css">.mostClicked::after{content:"Most clicked link";background-color:${colours.primary};color:white;border-radius:10px;padding:5px;font-size:0.75rem;white-space:nowrap;}.hotspot-container{cursor:pointer;position:relative;display:inline-block;vertical-align:middle;text-align:center;width:0;height:0;font-family:Arial,sans-serif}.hotspot{position:absolute;top:-50px;left:-50px}.total{background-color:rgba(255,0,0,.5);border-radius:50%}.no{position:absolute;height:20px;line-height:20px;width:20px;top:-10px;left:-10px;font-size:14px!important;color:#fff!important;font-style:normal!important;text-decoration:none!important;border-radius:50%;background-color:rgba(140,0,0,.8)}.zero{background-color:rgba(0,0,0,.8)!important}@media print{.hotspot-container{cursor:pointer;position:relative;display:inline-block;vertical-align:middle;text-align:center;width:0;height:0;font-family:Arial,sans-serif}.hotspot{position:absolute;top:-50px;left:-50px}.total{background-color:rgba(255,0,0,.5)!important;border-radius:50%}.zero{background-color:rgba(0,0,0,.8)!important}.no{position:absolute;height:20px;line-height:20px;width:20px;top:-10px;left:-10px;font-size:14px!important;color:#fff!important;font-style:normal!important;text-decoration:none!important;border-radius:50%;background-color:rgba(140,0,0,.8)!important}}.size-0{width:20px;height:20px;margin:40px}.size-1{width:20px;height:20px;margin:40px}.size-2,.size-3,.size-4,.size-5,.size-6,.size-7,.size-8,.size-9{width:28px;height:28px;margin:36px}.size-10,.size-11,.size-12,.size-13,.size-14,.size-15,.size-16,.size-17,.size-18,.size-19{width:36px;height:36px;margin:32px}.size-20,.size-21,.size-22,.size-23,.size-24,.size-25,.size-26,.size-27,.size-28,.size-29{width:44px;height:44px;margin:28px}.size-30,.size-31,.size-32,.size-33,.size-34,.size-35,.size-36,.size-37,.size-38,.size-39{width:52px;height:52px;margin:24px}.size-40,.size-41,.size-42,.size-43,.size-44,.size-45,.size-46,.size-47,.size-48,.size-49{width:60px;height:60px;margin:20px}.size-50,.size-51,.size-52,.size-53,.size-54,.size-55,.size-56,.size-57,.size-58,.size-59{width:68px;height:68px;margin:16px}.size-60,.size-61,.size-62,.size-63,.size-64,.size-65,.size-66,.size-67,.size-68,.size-69{width:76px;height:76px;margin:12px}.size-70,.size-71,.size-72,.size-73,.size-74,.size-75,.size-76,.size-77,.size-78,.size-79{width:84px;height:84px;margin:8px}.size-80,.size-81,.size-82,.size-83,.size-84,.size-85,.size-86,.size-87,.size-88,.size-89{width:92px;height:92px;margin:4px}.size-100,.size-90,.size-91,.size-92,.size-93,.size-94,.size-95,.size-96,.size-97,.size-98,.size-99{width:100px;height:100px}</style>`;

const HotspotPopover = ({ positionLeft, positionTop, open, onClose, links, id, onViewWhoClicked, type, editLink, setOpenEditLinkDialog }) => {
    let link = links.find(e => e.id.toString() === id);

    return (
        <Popover
            anchorReference="anchorPosition"
            anchorPosition={{ left: positionLeft, top: positionTop }}
            open={open}
            onClose={onClose}
        >
            <Box sx={{ padding: 2, width: '100vw', maxWidth: '500px' }}>
                <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    <Link
                        href={link.url}
                        target="_blank"
                        sx={{ whiteSpace: 'nowrap' }}
                        title={link.url}
                    >
                        {link.url}
                    </Link>
                </Box>
                {type === 'email' && <Box>Unique clicks: {link.uniqueClicks}</Box>}
                <Box>Total clicks: {link.totalClicks}</Box>
                {(onViewWhoClicked && link.totalClicks > 0) && (
                    <Button onClick={() => onViewWhoClicked(id)} size="small">
                        View who clicked
                    </Button>
                )}
                {editLink && (
                    <Button onClick={() => setOpenEditLinkDialog(true)} size="small">
                        Edit link
                    </Button>
                )}
            </Box>
        </Popover>
    );
};

const Hotspot = ({ html, itemLinks, onViewWhoClicked = null, type = 'email', campaignID, editLink = false, onRefetch }) => {
    const [selectedLink, setSelectedLink] = useState(null);
    const [links, setLinks] = useState(null);
    const [hotspotContainers, setHotspotContainers] = useState(null);
    const [openEditLinkDialog, setOpenEditLinkDialog] = useState(false);
    const [dialog, setDialog] = useState(null);

    window.addEventListener('resize', () => setSelectedLink(null));

    const getLinkId = href => {
        const patterns = type === 'page' ? pageUrlPatterns : emailUrlPatterns;

        let linkIdAsString = '';

        if (href.includes('@LINK')) {
            const linkParts = href.split('@');
            linkIdAsString = linkParts[1].replace('LINK', '');
        }
        else {
            let baseUrl = null;

            for (const pattern of patterns) {
                if (pattern.test(href)) {
                    baseUrl = href.match(pattern)[0];
                    break;
                }
            }

            if (baseUrl) {
                const linkParts = href
                    .replace(baseUrl, '')
                    .split('/')
                    .filter(s => s);

                linkIdAsString = type === 'email' ? linkParts[2] : linkParts[1];
            }
            else {
                return null;
            }
        }

        return parseInt(linkIdAsString);
    };

    const handleLoadHtml = async () => {
        const customElem = document.querySelector('shadow-root');
        const shadow = customElem.shadowRoot;

        const parser = new DOMParser();
        const parsedHtml = parser.parseFromString(html, 'text/html');

        parsedHtml.querySelector('head').insertAdjacentHTML('beforeend', style);

        const linkElements = parsedHtml.body.querySelectorAll('a[href]');

        const mostClickedLink = [...links].sort((a, b) => (a.totalClicks > b.totalClicks ? -1 : 1))[0];

        let currentIndex = 1;

        linkElements.forEach(({ href }) => {
            const linkId = getLinkId(href);
            const mostClicked = linkId === mostClickedLink?.id && mostClickedLink.totalClicks > 0;

            if (linkId) {

                const linkData = links.find(({ id }) => id === linkId);

                if (linkData) {
                    const hotspotSize = `size-${linkData.size}`;

                    const hotspot = `
                        <span class="hotspot-container" id="${idPrefix}${linkId}">
                            <span class="hotspot total ${hotspotSize}"></span>
                            <span class="hotspot no ${linkData.totalClicks === 0 ? 'zero' : ''} ${mostClicked ? 'mostClicked' : ''}">
                                <span>${currentIndex}</span>
                            </span>
                        </span>
                    `;

                    //add hotspot
                    parsedHtml.body.querySelector(`a[href="${href}"]`).insertAdjacentHTML('afterend', hotspot);

                    //insert real url
                    parsedHtml.body.querySelector(`a[href="${href}"]`).setAttribute('href', linkData.url);
                }

                currentIndex++;
            }
        });

        const serialiser = new XMLSerializer();
        const newHtml = serialiser.serializeToString(parsedHtml);

        shadow.innerHTML = newHtml;

        setHotspotContainers(shadow.querySelectorAll('span[class="hotspot-container"]'));
    };

    const handleProcessLinks = async () => {
        const totalClicks = itemLinks.map(e => e.totalClicks);
        const maxValue = Math.max(...totalClicks);

        const linksMapped = itemLinks.map(e => {
            let size = 0;

            if (maxValue > 0) {
                size = Math.ceil((e.totalClicks / maxValue) * 100);
            }

            if (size > 100) {
                size = 100;
            }

            return {
                id: e.id,
                url: e.url,
                uniqueClicks: e.uniqueClicks,
                totalClicks: e.totalClicks,
                size
            };
        });

        setLinks(linksMapped);
    };

    const handleClickHotspot = event => {
        const position = event.currentTarget.getBoundingClientRect();
        const id = event.currentTarget.id;
        const strippedId = id.replace(idPrefix, '');
        const url = links.find(link => link.id === Number(strippedId))?.url;

        setSelectedLink({
            id: strippedId,
            url: url,
            positionLeft: position.left,
            positionTop: position.top
        });
    };

    const handleSaveClose = () => {
        if (openEditLinkDialog) {
            onRefetch();
            setOpenEditLinkDialog(false);
        }

        setSelectedLink(null);
    };

    useEffect(() => {
        if (hotspotContainers) {
            for (let i = 0; i < hotspotContainers.length; i++) {
                hotspotContainers[i].addEventListener('click', handleClickHotspot);
            }
        }

        return () => {
            if (hotspotContainers) {
                for (let i = 0; i < hotspotContainers.length; i++) {
                    hotspotContainers[i].removeEventListener('click', handleClickHotspot);
                }
            }
        };
    }, [hotspotContainers]);

    useEffect(() => {
        links && handleLoadHtml();
    }, [links]);

    useEffect(() => {
        handleProcessLinks();
    }, [itemLinks]);

    useEffect(() => {
        handleProcessLinks();
    }, []);

    return (
        <shadow-root>
            <ReactShadowRoot />
            {selectedLink && (
                <HotspotPopover
                    id={selectedLink.id}
                    positionLeft={selectedLink.positionLeft}
                    positionTop={selectedLink.positionTop}
                    links={links}
                    open={Boolean(selectedLink)}
                    onClose={handleSaveClose}
                    onViewWhoClicked={onViewWhoClicked}
                    type={type}
                    editLink={editLink}
                    setOpenEditLinkDialog={setOpenEditLinkDialog}
                    campaignID={campaignID}
                    setDialog={setDialog}
                />
            )}
            {openEditLinkDialog && (
                <EditCampaignLinkDialog
                    campaignID={campaignID}
                    linkID={selectedLink.id}
                    url={selectedLink.url}
                    onSave={handleSaveClose}
                    onClose={handleSaveClose}
                />
            )}
            {dialog}
        </shadow-root>
    );
};

export default Hotspot;